const HEADING_SIZE_RATIO = 1.4;
const HEADING_MAX_WORDS = 14;

// Read a font size in pt from an inline style string, normalizing px → pt so all
// comparisons share one unit. Handles both `font-size:` and the `font:` shorthand.
function sizeFromStyle(style: string | null | undefined): number | undefined {
  if (!style) return undefined;
  let m = /font-size:\s*([\d.]+)\s*(pt|px)/i.exec(style);
  if (!m) m = /\bfont:\s*[^;]*?([\d.]+)\s*(pt|px)/i.exec(style);
  if (!m) return undefined;
  const v = parseFloat(m[1]);
  if (!isFinite(v)) return undefined;
  return m[2].toLowerCase() === 'px' ? v * 0.75 : v;
}

function median(nums: number[]): number | undefined {
  if (nums.length === 0) return undefined;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

const KEEP_INLINE = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'BR', 'IMG']);
const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'BLOCKQUOTE']);
const DROP_TAGS = new Set(['STYLE', 'META', 'LINK', 'TITLE', 'XML', 'SCRIPT', 'HEAD']);

function normalizeText(s: string): string {
  return s.replace(/\u00A0/g, ' ').replace(/&nbsp;/g, ' ');
}

function visibleText(el: Element): string {
  return normalizeText(el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function wordCount(s: string): number {
  return s ? s.split(/\s+/).filter(Boolean).length : 0;
}

// The body's baseline font size: a text-length-weighted median over blocks, so the
// high-volume body text sets the baseline and a few large heading lines don't skew
// it (a plain unweighted median is pulled up by short headings on small inputs).
function baselineSize(blocks: Element[]): number | undefined {
  const samples: { size: number; weight: number }[] = [];
  for (const el of blocks) {
    if (el.tagName === 'UL' || el.tagName === 'OL') continue;
    const size = blockSize(el);
    if (size === undefined) continue;
    samples.push({ size, weight: Math.max(visibleText(el).length, 1) });
  }
  if (samples.length === 0) return undefined;
  samples.sort((a, b) => a.size - b.size);
  const total = samples.reduce((sum, s) => sum + s.weight, 0);
  let cum = 0;
  for (const s of samples) {
    cum += s.weight;
    if (cum >= total / 2) return s.size;
  }
  return samples[samples.length - 1].size;
}

// The largest inline size anywhere in a block (heading text is uniformly large,
// so max is a safe representative).
function blockSize(block: Element): number | undefined {
  const sizes: number[] = [];
  const own = sizeFromStyle(block.getAttribute('style'));
  if (own !== undefined) sizes.push(own);
  block.querySelectorAll('*').forEach((el) => {
    const sz = sizeFromStyle(el.getAttribute('style'));
    if (sz !== undefined) sizes.push(sz);
  });
  return sizes.length ? Math.max(...sizes) : undefined;
}

function styleIsBold(style: string | null | undefined): boolean {
  return !!style && /font-weight:\s*(bold|[6-9]00)\b/i.test(style);
}

// Google Docs wraps the whole selection in <b style="font-weight:normal">; unwrap
// any bold tag whose style actually sets a non-bold weight so it can't mark
// everything bold.
function stripFakeBold(scope: Element): void {
  for (const el of Array.from(scope.querySelectorAll('b, strong'))) {
    const st = el.getAttribute('style');
    if (st && /font-weight:\s*(normal|[1-5]00)\b/i.test(st)) {
      const parent = el.parentNode;
      if (!parent) continue;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
  }
}

// Google Docs conveys emphasis as inline styles, not tags. Convert style-based
// bold / italic into real <b>/<i> wrappers so the later junk-strip keeps them.
function applyStyleEmphasis(scope: Element): void {
  const doc = scope.ownerDocument;
  if (!doc) return;
  for (const el of Array.from(scope.querySelectorAll('[style]'))) {
    const st = el.getAttribute('style') || '';
    const tag = el.tagName;
    const bold = /font-weight:\s*(bold|[6-9]00)\b/i.test(st) && tag !== 'B' && tag !== 'STRONG';
    const italic = /font-style:\s*italic\b/i.test(st) && tag !== 'I' && tag !== 'EM';
    if (!bold && !italic) continue;
    const frag = doc.createDocumentFragment();
    while (el.firstChild) frag.appendChild(el.firstChild);
    let node: Node = frag;
    if (italic) {
      const i = doc.createElement('i');
      i.appendChild(node);
      node = i;
    }
    if (bold) {
      const b = doc.createElement('b');
      b.appendChild(node);
      node = b;
    }
    el.appendChild(node);
  }
}

// Heading levels for real source heading tags (Google Docs). Post title owns h1.
const HEADING_TAG_LEVEL: Record<string, 2 | 3> = {
  H1: 2,
  H2: 2,
  H3: 3,
  H4: 3,
  H5: 3,
  H6: 3,
};

// A block is "bold" only when every non-blank text node sits under a bold
// ancestor — this distinguishes a bold heading from a paragraph with one bold word.
function isBlockBold(block: Element): boolean {
  const doc = block.ownerDocument;
  if (!doc) return false;
  const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let sawText = false;
  let node = walker.nextNode();
  while (node) {
    if (normalizeText(node.textContent ?? '').trim()) {
      sawText = true;
      let bold = false;
      let cur: Element | null = node.parentElement;
      while (cur && cur !== block.parentElement) {
        if (cur.tagName === 'B' || cur.tagName === 'STRONG' || styleIsBold(cur.getAttribute('style'))) {
          bold = true;
          break;
        }
        cur = cur.parentElement;
      }
      if (!bold) return false;
    }
    node = walker.nextNode();
  }
  return sawText;
}

// Strip a block down to allowed inline tags with no attributes (href kept on <a>).
// stripBold unwraps <b>/<strong> too — used for headings, which are already bold.
function cleanInner(block: Element, stripBold: boolean): string {
  const clone = block.cloneNode(true) as Element;
  applyStyleEmphasis(clone); // Google Docs: style-based bold/italic → <b>/<i>
  // Serialize from a wrapper so a block that is itself a kept tag — a bare
  // top-level <img> from an editor-to-editor copy — is cleaned and emitted
  // rather than reduced to its empty innerHTML.
  const wrap = (block.ownerDocument ?? document).createElement('div');
  wrap.appendChild(clone);
  for (const el of Array.from(wrap.querySelectorAll('*'))) {
    const tag = el.tagName;
    if (DROP_TAGS.has(tag)) {
      el.remove();
      continue;
    }
    const keep = KEEP_INLINE.has(tag) && !(stripBold && (tag === 'B' || tag === 'STRONG'));
    if (keep) {
      for (const attr of Array.from(el.attributes)) {
        const keepAttr =
          (tag === 'A' && attr.name === 'href') || (tag === 'IMG' && attr.name === 'src');
        if (!keepAttr) el.removeAttribute(attr.name);
      }
    } else {
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
    }
  }
  return normalizeText(wrap.innerHTML).replace(/\s+/g, ' ').trim();
}

function renderList(list: Element): string {
  const tag = list.tagName.toLowerCase(); // 'ul' | 'ol'
  const lis = Array.from(list.children)
    .filter((c) => c.tagName === 'LI')
    .map((li) => cleanInner(li, false))
    .filter(Boolean)
    .map((h) => `<li>${h}</li>`);
  return lis.length ? `<${tag}>${lis.join('')}</${tag}>` : '';
}

// Flatten the body to a list of block elements, descending through generic
// wrappers (DIV/SECTION) that Word/Pages sometimes add.
function collectBlocks(root: Element): Element[] {
  const blocks: Element[] = [];
  for (const child of Array.from(root.children)) {
    const tag = child.tagName;
    if (DROP_TAGS.has(tag)) continue;
    if (BLOCK_TAGS.has(tag)) {
      blocks.push(child);
    } else if (Array.from(child.children).some((c) => BLOCK_TAGS.has(c.tagName) || c.tagName === 'DIV')) {
      blocks.push(...collectBlocks(child));
    } else {
      blocks.push(child); // generic wrapper with only inline content → a paragraph
    }
  }
  return blocks;
}

export function cleanWordHtml(html: string): string {
  if (!html || !html.trim()) return '';

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return regexFallback(html);
  }
  const body = doc.body;
  if (!body) return regexFallback(html);

  // Remove HTML comments (Word wraps conditional cruft in them).
  const cw = doc.createTreeWalker(body, NodeFilter.SHOW_COMMENT);
  const comments: Node[] = [];
  let cn = cw.nextNode();
  while (cn) {
    comments.push(cn);
    cn = cw.nextNode();
  }
  comments.forEach((n) => n.parentNode?.removeChild(n));

  stripFakeBold(body); // Google Docs: neutralize the normal-weight wrapper <b>

  const blocks = collectBlocks(body);

  // A clipboard with bare text (no element wrapper) still has body.textContent.
  if (blocks.length === 0) {
    const t = visibleText(body);
    return t ? `<p>${t}</p>` : '';
  }

  const baseline = baselineSize(blocks);

  // level is set for source heading tags (Google Docs); size-detected headings
  // (Word) leave it undefined and get tiered by font size below.
  type Cls = { kind: 'p' | 'h' | 'list'; size: number; el: Element; level?: 2 | 3 };
  const classified: Cls[] = [];
  for (const el of blocks) {
    const tag = el.tagName;
    if (tag === 'UL' || tag === 'OL') {
      classified.push({ kind: 'list', size: 0, el });
      continue;
    }
    const text = visibleText(el);
    // querySelector never matches the element itself, so a bare top-level <img>
    // block (an editor-to-editor copy) must be checked by tag as well.
    const hasImg = el.tagName === 'IMG' || el.querySelector('img') !== null;
    if (!text && !hasImg) continue; // drop empty / spacer, but keep image-only blocks
    const size = blockSize(el) ?? baseline ?? 0;
    // Real heading tags (Google Docs) win regardless of size.
    if (HEADING_TAG_LEVEL[tag]) {
      classified.push({ kind: 'h', size, el, level: HEADING_TAG_LEVEL[tag] });
      continue;
    }
    const isHeading =
      baseline !== undefined &&
      size >= baseline * HEADING_SIZE_RATIO &&
      wordCount(text) <= HEADING_MAX_WORDS &&
      isBlockBold(el);
    classified.push({ kind: isHeading ? 'h' : 'p', size, el });
  }

  // Tier the size-detected headings: the largest distinct size is h2, smaller h3.
  const headingSizes = Array.from(
    new Set(classified.filter((c) => c.kind === 'h' && c.level === undefined).map((c) => c.size)),
  ).sort((a, b) => b - a);
  const levelFor = (size: number): 2 | 3 => (headingSizes.indexOf(size) <= 0 ? 2 : 3);

  const out: string[] = [];
  for (const c of classified) {
    if (c.kind === 'list') {
      const list = renderList(c.el);
      if (list) out.push(list);
    } else if (c.kind === 'h') {
      const inner = cleanInner(c.el, true);
      if (inner) {
        const lvl = c.level ?? levelFor(c.size);
        out.push(`<h${lvl}>${inner}</h${lvl}>`);
      }
    } else {
      const inner = cleanInner(c.el, false);
      if (inner) out.push(`<p>${inner}</p>`);
    }
  }
  return out.join('');
}

function regexFallback(html: string): string {
  return normalizeText(html)
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\/?o:p>/gi, '')
    .replace(/\s+style="[^"]*"/gi, '')
    .replace(/\s+class="[^"]*"/gi, '');
}

// Exposed for unit tests only.
export const __test = { sizeFromStyle, median, HEADING_SIZE_RATIO, HEADING_MAX_WORDS };
